'use client';
import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { useLiveSpots } from '@/hooks/useLiveSpots';

export default function LandingHero() {
  const { spotsRemaining, spotsTaken, totalSpots, isLoaded } = useLiveSpots();

  const percentTaken = (spotsTaken / totalSpots) * 100;

  if (isLoaded && spotsRemaining === 0) {
    return (
      <section className="relative pt-32 pb-24 px-6 overflow-hidden">
        <div className="max-w-screen-xl mx-auto relative text-center">
          <h1 className="font-display font-bold text-5xl text-white mb-6">
            All 500 founding spots have been claimed.
          </h1>
          <p className="text-lg text-[#9CA3AF] mb-8">Join the waitlist to be notified when new spots open.</p>
          <input
            type="email"
            placeholder="Enter your email"
            className="input-field max-w-sm mx-auto block mb-4"
          />
          <button className="btn-primary justify-center">Join Waitlist</button>
        </div>
      </section>
    );
  }

  return (
    <section className="relative pt-32 pb-24 px-6 overflow-hidden">
      {/* Amber gradient mesh */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] pointer-events-none">
        <div className="absolute top-[-100px] right-[-100px] w-[500px] h-[500px] bg-[#F59E0B]/8 rounded-full blur-[120px]" />
        <div className="absolute top-[100px] right-[50px] w-[300px] h-[300px] bg-[#F59E0B]/5 rounded-full blur-[80px]" />
      </div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] pointer-events-none">
        <div className="absolute bottom-[-50px] left-[-50px] w-[350px] h-[350px] bg-[#F59E0B]/4 rounded-full blur-[100px]" />
      </div>

      <div className="max-w-screen-xl mx-auto relative">
        {/* Spots counter pill */}
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] border border-[#2A2A2A] rounded-full mb-8">
          <span className={`w-2 h-2 rounded-full animate-pulse ${isLoaded && spotsRemaining <= 50 ? 'bg-[#EF4444]' : 'bg-[#F59E0B]'}`} />
          <span className="text-sm font-medium text-white font-mono">
            {isLoaded ? (
              <>{spotsRemaining} of {totalSpots} spots remaining</>
            ) : (
              <span className="animate-pulse">Loading spots…</span>
            )}
          </span>
          <span className="text-xs text-[#6B7280]">— Founding members only</span>
        </div>

        {/* Headline */}
        <h1 className="font-display font-bold text-5xl md:text-6xl lg:text-7xl text-white leading-[1.05] max-w-3xl mb-6">
          Refer a Driver.<br />
          <span className="text-[#F59E0B]">Earn Every Delivery.</span><br />
          Forever.
        </h1>

        <p className="text-lg md:text-xl text-[#9CA3AF] max-w-2xl mb-10 leading-relaxed font-body">
          Join 500 founding members of the BidTrek Partner Network. Pay once. Earn every month for as long as your drivers deliver building materials across Saint Lucia.
        </p>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-12">
          <Link
            href="/member-sign-up-flow"
            className="btn-primary text-base px-8 py-4 amber-glow-lg"
          >
            <Icon name="BoltIcon" size={20} variant="solid" />
            Claim Your Spot — EC$499
          </Link>
          <p className="text-sm text-[#6B7280]">
            One-time payment. Lifetime earnings. No monthly fees.
          </p>
        </div>

        {/* Progress bar */}
        <div className="max-w-md">
          <div className="flex justify-between text-xs text-[#6B7280] mb-2 font-mono">
            <span>{isLoaded ? spotsTaken : '—'} spots claimed</span>
            <span>{isLoaded ? spotsRemaining : '—'} remaining</span>
          </div>
          <div className="h-2 bg-[#2A2A2A] rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#D97706] to-[#F59E0B] rounded-full transition-all duration-1000"
              style={{ width: isLoaded ? `${percentTaken}%` : '0%' }}
            />
          </div>
          <p className="text-xs text-[#6B7280] mt-2">
            Once 500 spots are filled, this offer closes permanently.
          </p>
        </div>

        {/* Trust signals */}
        <div className="flex flex-wrap items-center gap-6 mt-10 pt-10 border-t border-[#2A2A2A]">
          {[
            { icon: 'ShieldCheckIcon', label: 'Payments secured by Stripe' },
            { icon: 'LockClosedIcon', label: 'Bank details encrypted' },
            { icon: 'CalendarIcon', label: 'Paid every month' },
            { icon: 'MapPinIcon', label: 'Saint Lucia only' },
          ].map((item) => (
            <div key={`trust-${item.label}`} className="flex items-center gap-2 text-sm text-[#6B7280]">
              <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={16} className="text-[#F59E0B]" />
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}